import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  TextField,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Stack,
  Snackbar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TimelineIcon from '@mui/icons-material/Timeline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FlagIcon from '@mui/icons-material/Flag';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EventIcon from '@mui/icons-material/Event';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { 
  directDocumentQuery, 
  extractProjectTimeline, 
  processProject
} from '../../utils/aiService';
import { getProjectTimeline, saveProjectTimeline, fetchTimelineFromSOW } from '../../utils/preparePhaseService';
import { format, compareAsc, differenceInDays, addDays } from 'date-fns';
import SafetyConfirmDialog from '../../components/SafetyConfirmDialog';

// Styled components
const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    marginRight: theme.spacing(1),
  },
}));

// Custom timeline phase component
const TimelinePhase = styled(Box)(({ theme, phasecolor }) => ({
  height: '30px',
  backgroundColor: phasecolor || theme.palette.primary.main,
  borderRadius: theme.shape.borderRadius,
  position: 'relative',
  margin: '0 4px',
  '&::after': {
    content: '""',
    position: 'absolute',
    right: '-8px',
    top: '50%',
    transform: 'translateY(-50%)',
    borderTop: '8px solid transparent',
    borderBottom: '8px solid transparent',
    borderLeft: `8px solid ${phasecolor || theme.palette.primary.main}`,
  }
}));

// Phase colors
const phaseColors = {
  'Prepare': '#3f51b5', // indigo
  'Explore': '#2196f3', // blue
  'Realize': '#00bcd4', // cyan
  'Deploy': '#ff9800', // orange
  'Run': '#4caf50', // green
};

// Milestone icons
const milestoneTypeIcons = {
  'Project Milestone': <FlagIcon />,
  'Deliverable': <AssignmentIcon />,
  'Approval': <CheckCircleOutlineIcon />,
  'Meeting': <EventIcon />
};

const ProjectTimelinePage = () => {
  const { projectId } = useParams();
  const { project, refreshOverview } = useOutletContext();
  const [fetchConfirmOpen, setFetchConfirmOpen] = useState(false);
  
  const [timeline, setTimeline] = useState({
    overview: '',
    startDate: new Date(),
    endDate: addDays(new Date(), 180), // Default 6 months
    phases: [],
    milestones: []
  });
  
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState(null);
  const [primarySource, setPrimarySource] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Edit states
  const [editMode, setEditMode] = useState(false);
  const [editingTimeline, setEditingTimeline] = useState({
    overview: '',
    startDate: new Date(),
    endDate: addDays(new Date(), 180),
    phases: [],
    milestones: []
  });
  
  // Dialog states
  const [openPhaseDialog, setOpenPhaseDialog] = useState(false);
  const [openMilestoneDialog, setOpenMilestoneDialog] = useState(false);
  const [phaseItem, setPhaseItem] = useState({
    id: '',
    name: '',
    startDate: new Date(),
    endDate: addDays(new Date(), 30),
    description: ''
  });
  const [milestoneItem, setMilestoneItem] = useState({
    id: '',
    name: '',
    date: new Date(),
    type: 'Project Milestone',
    description: '',
    deliverables: ''
  });
  const [dialogItemIndex, setDialogItemIndex] = useState(null);
  
  // Function to validate and normalize timeline data structure
  const validateTimelineData = (timelineData) => {
    // Deep clone to avoid reference issues
    const validatedData = JSON.parse(JSON.stringify(timelineData));
    const today = new Date();
    
    // Ensure overview exists and is a string
    if (!validatedData.overview || typeof validatedData.overview !== 'string') {
      validatedData.overview = validatedData.overview_statement || validatedData.timeline_overview || 
        'Project timeline for SAP implementation with standard implementation phases.';
    }
    
    // Ensure dates are valid Date objects
    if (!validatedData.startDate || !(validatedData.startDate instanceof Date || typeof validatedData.startDate === 'string')) {
      validatedData.startDate = today;
    } else if (typeof validatedData.startDate === 'string') {
      try {
        const parsedDate = new Date(validatedData.startDate);
        if (!isNaN(parsedDate.getTime())) {
          validatedData.startDate = parsedDate;
        } else {
          validatedData.startDate = today;
        }
      } catch (e) {
        validatedData.startDate = today;
      }
    }
    
    if (!validatedData.endDate || !(validatedData.endDate instanceof Date || typeof validatedData.endDate === 'string')) {
      validatedData.endDate = addDays(today, 180); // Default 6 months
    } else if (typeof validatedData.endDate === 'string') {
      try {
        const parsedDate = new Date(validatedData.endDate);
        if (!isNaN(parsedDate.getTime())) {
          validatedData.endDate = parsedDate;
        } else {
          validatedData.endDate = addDays(today, 180);
        }
      } catch (e) {
        validatedData.endDate = addDays(today, 180);
      }
    }
    
    // Ensure phases array exists and validate each phase
    if (!validatedData.phases || !Array.isArray(validatedData.phases)) {
      validatedData.phases = [];
    }
    
    validatedData.phases = validatedData.phases.map((phase, index) => {
      const validPhase = {...phase};
      
      // Ensure ID exists
      if (!validPhase.id || typeof validPhase.id !== 'string') {
        validPhase.id = `PHASE-${String(index + 1).padStart(3, '0')}`;
      }
      
      // Ensure name exists
      if (!validPhase.name || typeof validPhase.name !== 'string') {
        // Try to determine a standard SAP phase name based on order
        const standardPhases = ['Prepare', 'Explore', 'Realize', 'Deploy', 'Run'];
        validPhase.name = standardPhases[index % standardPhases.length] || `Phase ${index + 1}`;
      }
      
      // Normalize date fields
      if (!validPhase.startDate || !(validPhase.startDate instanceof Date || typeof validPhase.startDate === 'string')) {
        // Use start_date if available, otherwise calculate based on index
        if (validPhase.start_date) {
          try {
            const parsedDate = new Date(validPhase.start_date);
            if (!isNaN(parsedDate.getTime())) {
              validPhase.startDate = parsedDate;
            } else {
              validPhase.startDate = addDays(validatedData.startDate, index * 30);
            }
          } catch (e) {
            validPhase.startDate = addDays(validatedData.startDate, index * 30);
          }
        } else {
          validPhase.startDate = addDays(validatedData.startDate, index * 30);
        }
      } else if (typeof validPhase.startDate === 'string') {
        try {
          const parsedDate = new Date(validPhase.startDate);
          if (!isNaN(parsedDate.getTime())) {
            validPhase.startDate = parsedDate;
          } else {
            validPhase.startDate = addDays(validatedData.startDate, index * 30);
          }
        } catch (e) {
          validPhase.startDate = addDays(validatedData.startDate, index * 30);
        }
      }
      
      if (!validPhase.endDate || !(validPhase.endDate instanceof Date || typeof validPhase.endDate === 'string')) {
        // Use end_date if available, otherwise calculate based on index
        if (validPhase.end_date) {
          try {
            const parsedDate = new Date(validPhase.end_date);
            if (!isNaN(parsedDate.getTime())) {
              validPhase.endDate = parsedDate;
            } else {
              validPhase.endDate = addDays(validPhase.startDate, 30);
            }
          } catch (e) {
            validPhase.endDate = addDays(validPhase.startDate, 30);
          }
        } else {
          validPhase.endDate = addDays(validPhase.startDate, 30);
        }
      } else if (typeof validPhase.endDate === 'string') {
        try {
          const parsedDate = new Date(validPhase.endDate);
          if (!isNaN(parsedDate.getTime())) {
            validPhase.endDate = parsedDate;
          } else {
            validPhase.endDate = addDays(validPhase.startDate, 30);
          }
        } catch (e) {
          validPhase.endDate = addDays(validPhase.startDate, 30);
        }
      }
      
      // Ensure description exists
      if (!validPhase.description || typeof validPhase.description !== 'string') {
        validPhase.description = validPhase.desc || '';
      }
      
      return validPhase;
    });
    
    // Sort phases by start date
    validatedData.phases.sort((a, b) => compareAsc(new Date(a.startDate), new Date(b.startDate)));
    
    // Ensure milestones array exists and validate each milestone
    if (!validatedData.milestones || !Array.isArray(validatedData.milestones)) {
      validatedData.milestones = [];
    }
    
    validatedData.milestones = validatedData.milestones.map((milestone, index) => {
      const validMilestone = {...milestone};
      
      // Ensure ID exists
      if (!validMilestone.id || typeof validMilestone.id !== 'string') {
        validMilestone.id = `MS-${String(index + 1).padStart(3, '0')}`;
      }
      
      // Ensure name exists
      if (!validMilestone.name || typeof validMilestone.name !== 'string') {
        validMilestone.name = `Milestone ${index + 1}`;
      }
      
      // Normalize date field
      if (!validMilestone.date || !(validMilestone.date instanceof Date || typeof validMilestone.date === 'string')) {
        // Calculate milestone date based on phases if available
        if (validatedData.phases && validatedData.phases.length > 0) {
          const phaseIndex = Math.min(index, validatedData.phases.length - 1);
          const phase = validatedData.phases[phaseIndex];
          const phaseDuration = differenceInDays(phase.endDate, phase.startDate);
          validMilestone.date = addDays(phase.startDate, Math.floor(phaseDuration * 0.7));
        } else {
          validMilestone.date = addDays(validatedData.startDate, (index + 1) * 30);
        }
      } else if (typeof validMilestone.date === 'string') {
        try {
          const parsedDate = new Date(validMilestone.date);
          if (!isNaN(parsedDate.getTime())) {
            validMilestone.date = parsedDate;
          } else {
            validMilestone.date = addDays(validatedData.startDate, (index + 1) * 30);
          }
        } catch (e) {
          validMilestone.date = addDays(validatedData.startDate, (index + 1) * 30);
        }
      }
      
      // Ensure type exists and is valid
      const validTypes = ['Project Milestone', 'Deliverable', 'Approval', 'Meeting'];
      if (!validMilestone.type || typeof validMilestone.type !== 'string' || !validTypes.includes(validMilestone.type)) {
        validMilestone.type = 'Project Milestone';
      }
      
      // Ensure description exists
      if (!validMilestone.description || typeof validMilestone.description !== 'string') {
        validMilestone.description = validMilestone.desc || '';
      }
      
      // Ensure deliverables exists
      if (!validMilestone.deliverables || typeof validMilestone.deliverables !== 'string') {
        validMilestone.deliverables = '';
      }
      
      return validMilestone;
    });
    
    // Sort milestones by date
    validatedData.milestones.sort((a, b) => compareAsc(new Date(a.date), new Date(b.date)));
    
    return validatedData;
  };

  // Load project timeline from database when component mounts
  useEffect(() => {
    if (projectId) {
      loadProjectTimelineFromDB();
    }
  }, [projectId]);
  
  // Load project timeline from database
  const loadProjectTimelineFromDB = async () => {
    setLoading(true);
    try {
      const timelineData = await getProjectTimeline(projectId);
      
      if (timelineData && timelineData.overview !== undefined) {
        // We have data in the database - validate and set it
        const validatedTimeline = validateTimelineData(timelineData);
        setTimeline(validatedTimeline);
        setEditingTimeline(validatedTimeline);
      } else {
        // No data in database, set empty state
        const emptyTimeline = {
          overview: '',
          startDate: new Date(),
          endDate: addDays(new Date(), 180),
          phases: [],
          milestones: []
        };
        setTimeline(emptyTimeline);
        setEditingTimeline(emptyTimeline);
      }
    } catch (error) {
      console.error('Error loading project timeline from database:', error);
      // Set empty state on error
      const emptyTimeline = {
        overview: '',
        startDate: new Date(),
        endDate: addDays(new Date(), 180),
        phases: [],
        milestones: []
      };
      setTimeline(emptyTimeline);
      setEditingTimeline(emptyTimeline);
    } finally {
      setLoading(false);
    }
  };
  
  
  // Get the SOW document from the project
  const getSowDocument = () => {
    if (project && project.documents && project.documents.sow && project.documents.sow.length > 0) {
      return project.documents.sow[0];
    }
    return null;
  };
  
  // Fetch project timeline from project documents and save to database
  const generateTimelineWithAI = async () => {
    if (!projectId) return;

    setAiLoading(true);
    setAiResponse(null);

    try {
      const result = await fetchTimelineFromSOW(projectId);

      if (result && result.data) {
        const fetched = validateTimelineData(result.data);
        setTimeline(fetched);
        setEditingTimeline(fetched);

        if (result.source) {
          setPrimarySource(result.source);
        }

        setSnackbar({
          open: true,
          message: 'Project timeline successfully fetched from project documents and saved.',
          severity: 'success'
        });

        setEditMode(true);
      } else {
        setSnackbar({
          open: true,
          message: result?.error || 'Failed to extract project timeline from project documents.',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error("Error fetching project timeline from project documents:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Error fetching project timeline from project documents.',
        severity: 'error'
      });
    } finally {
      setAiLoading(false);
    }
  };
  
  
  // Enter edit mode
  const handleEdit = () => {
    setEditingTimeline({...timeline});
    setEditMode(true);
  };
  
  // Cancel edit mode
  const handleCancel = () => {
    setEditingTimeline({...timeline});
    setEditMode(false);
  };
  
  // Save changes to database
  const handleSave = async () => {
    setLoading(true);
    try {
      await saveProjectTimeline(projectId, editingTimeline);
      
      setTimeline({...editingTimeline});
      setEditMode(false);
      if (refreshOverview) refreshOverview();
      
      setSnackbar({
        open: true,
        message: 'Project timeline saved successfully.',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving project timeline:', error);
      setSnackbar({
        open: true,
        message: 'Error saving project timeline. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle overview change
  const handleOverviewChange = (e) => {
    setEditingTimeline(prevState => ({
      ...prevState,
      overview: e.target.value
    }));
  };
  
  // Handle project date change
  const handleProjectDateChange = (field, date) => {
    setEditingTimeline(prevState => ({
      ...prevState,
      [field]: date
    }));
  };
  
  // Open phase dialog
  const handleOpenPhaseDialog = (index = null) => {
    setDialogItemIndex(index);
    
    if (index !== null) {
      // Edit existing phase
      setPhaseItem({...editingTimeline.phases[index]});
    } else {
      // Add new phase - generate a new ID and calculate dates
      const newId = generateNewPhaseId();
      const lastPhase = editingTimeline.phases.length > 0 
        ? editingTimeline.phases[editingTimeline.phases.length - 1] 
        : null;
      
      const startDate = lastPhase 
        ? addDays(new Date(lastPhase.endDate), 1) 
        : new Date(editingTimeline.startDate);
      
      const endDate = addDays(startDate, 30);
      
      setPhaseItem({
        id: newId,
        name: '',
        startDate,
        endDate,
        description: ''
      });
    }
    
    setOpenPhaseDialog(true);
  };
  
  // Open milestone dialog
  const handleOpenMilestoneDialog = (index = null) => {
    setDialogItemIndex(index);
    
    if (index !== null) {
      // Edit existing milestone
      setMilestoneItem({...editingTimeline.milestones[index]});
    } else {
      // Add new milestone - generate a new ID
      const newId = generateNewMilestoneId();
      
      setMilestoneItem({
        id: newId,
        name: '',
        date: new Date(editingTimeline.startDate),
        type: 'Project Milestone',
        description: '',
        deliverables: ''
      });
    }
    
    setOpenMilestoneDialog(true);
  };
  
  // Generate a new phase ID
  const generateNewPhaseId = () => {
    const existingIds = editingTimeline.phases.map(item => item.id);
    
    // Find the highest existing ID number and increment by 1
    let highestNum = 0;
    existingIds.forEach(id => {
      const match = id.match(/^PHASE-(\d+)$/);
      if (match) {
        const num = parseInt(match[1], 10);
        if (num > highestNum) highestNum = num;
      }
    });
    
    // Format with leading zeros
    const nextNum = highestNum + 1;
    const paddedNum = nextNum.toString().padStart(3, '0');
    
    return `PHASE-${paddedNum}`;
  };
  
  // Generate a new milestone ID
  const generateNewMilestoneId = () => {
    const existingIds = editingTimeline.milestones.map(item => item.id);
    
    // Find the highest existing ID number and increment by 1
    let highestNum = 0;
    existingIds.forEach(id => {
      const match = id.match(/^MS-(\d+)$/);
      if (match) {
        const num = parseInt(match[1], 10);
        if (num > highestNum) highestNum = num;
      }
    });
    
    // Format with leading zeros
    const nextNum = highestNum + 1;
    const paddedNum = nextNum.toString().padStart(3, '0');
    
    return `MS-${paddedNum}`;
  };
  
  // Close dialogs
  const handleClosePhaseDialog = () => {
    setOpenPhaseDialog(false);
  };
  
  const handleCloseMilestoneDialog = () => {
    setOpenMilestoneDialog(false);
  };
  
  // Handle dialog input change
  const handlePhaseInputChange = (e) => {
    const { name, value } = e.target;
    setPhaseItem(prevState => ({
      ...prevState,
      [name]: value
    }));
  };
  
  const handleMilestoneInputChange = (e) => {
    const { name, value } = e.target;
    setMilestoneItem(prevState => ({
      ...prevState,
      [name]: value
    }));
  };
  
  // Handle date changes
  const handlePhaseDate = (field, date) => {
    setPhaseItem(prevState => ({
      ...prevState,
      [field]: date
    }));
  };
  
  const handleMilestoneDate = (date) => {
    setMilestoneItem(prevState => ({
      ...prevState,
      date
    }));
  };
  
  // Save dialog items
  const handleSavePhase = () => {
    if (!phaseItem.name.trim() || !phaseItem.id.trim()) {
      return;
    }
    
    const updatedTimeline = {...editingTimeline};
    
    if (dialogItemIndex === null) {
      // Add new phase
      updatedTimeline.phases = [...updatedTimeline.phases, {...phaseItem}];
    } else {
      // Edit existing phase
      updatedTimeline.phases[dialogItemIndex] = {...phaseItem};
    }
    
    // Sort phases by start date
    updatedTimeline.phases.sort((a, b) => compareAsc(new Date(a.startDate), new Date(b.startDate)));
    
    // Update project start and end dates if needed
    if (updatedTimeline.phases.length > 0) {
      const firstPhase = updatedTimeline.phases[0];
      const lastPhase = updatedTimeline.phases[updatedTimeline.phases.length - 1];
      
      // Update project start date if the first phase starts earlier
      if (compareAsc(new Date(firstPhase.startDate), new Date(updatedTimeline.startDate)) < 0) {
        updatedTimeline.startDate = new Date(firstPhase.startDate);
      }
      
      // Update project end date if the last phase ends later
      if (compareAsc(new Date(lastPhase.endDate), new Date(updatedTimeline.endDate)) > 0) {
        updatedTimeline.endDate = new Date(lastPhase.endDate);
      }
    }
    
    setEditingTimeline(updatedTimeline);
    handleClosePhaseDialog();
  };
  
  const handleSaveMilestone = () => {
    if (!milestoneItem.name.trim() || !milestoneItem.id.trim()) {
      return;
    }
    
    const updatedTimeline = {...editingTimeline};
    
    if (dialogItemIndex === null) {
      // Add new milestone
      updatedTimeline.milestones = [...updatedTimeline.milestones, {...milestoneItem}];
    } else {
      // Edit existing milestone
      updatedTimeline.milestones[dialogItemIndex] = {...milestoneItem};
    }
    
    // Sort milestones by date
    updatedTimeline.milestones.sort((a, b) => compareAsc(new Date(a.date), new Date(b.date)));
    
    setEditingTimeline(updatedTimeline);
    handleCloseMilestoneDialog();
  };
  
  // Delete items
  const handleDeletePhase = (index) => {
    const updatedTimeline = {...editingTimeline};
    updatedTimeline.phases = updatedTimeline.phases.filter((_, i) => i !== index);
    setEditingTimeline(updatedTimeline);
  };
  
  const handleDeleteMilestone = (index) => {
    const updatedTimeline = {...editingTimeline};
    updatedTimeline.milestones = updatedTimeline.milestones.filter((_, i) => i !== index);
    setEditingTimeline(updatedTimeline);
  };
  
  // Format date for display
  const formatDate = (date) => {
    if (!date) return '';
    return format(new Date(date), 'MMM d, yyyy');
  };
  
  // Calculate duration in days
  const calculateDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    return differenceInDays(new Date(endDate), new Date(startDate)) + 1;
  };
  
  // Render phases table
  const renderPhasesTable = () => {
    const phases = editMode ? (editingTimeline.phases || []) : (timeline.phases || []);
    
    return (
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'action.hover' }}>
              <TableCell width="10%"><strong>ID</strong></TableCell>
              <TableCell width="20%"><strong>Phase</strong></TableCell>
              <TableCell width="15%"><strong>Start Date</strong></TableCell>
              <TableCell width="15%"><strong>End Date</strong></TableCell>
              <TableCell width="10%"><strong>Duration</strong></TableCell>
              <TableCell width="20%"><strong>Description</strong></TableCell>
              {editMode && <TableCell width="10%" align="center"><strong>Actions</strong></TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {phases.map((item, index) => (
              <TableRow key={index} hover>
                <TableCell>{item.id || ''}</TableCell>
                <TableCell>
                  <Chip 
                    label={item.name || ''} 
                    sx={{ 
                      backgroundColor: phaseColors[item.name] || 'primary.main',
                      color: 'white',
                      fontWeight: 'bold'
                    }}
                  />
                </TableCell>
                <TableCell>{formatDate(item.startDate)}</TableCell>
                <TableCell>{formatDate(item.endDate)}</TableCell>
                <TableCell>{calculateDuration(item.startDate, item.endDate)} days</TableCell>
                <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  {item.description || ''}
                </TableCell>
                {editMode && (
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpenPhaseDialog(index)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleDeletePhase(index)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {editMode && (
              <TableRow>
                <TableCell colSpan={editMode ? 7 : 6} align="center">
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenPhaseDialog()}
                    variant="outlined"
                    size="small"
                    sx={{ my: 1 }}
                  >
                    Add Phase
                  </Button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };
  
  // Render milestones table
  const renderMilestonesTable = () => {
    const milestones = editMode ? (editingTimeline.milestones || []) : (timeline.milestones || []);
    
    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'action.hover' }}>
              <TableCell width="10%"><strong>ID</strong></TableCell>
              <TableCell width="20%"><strong>Milestone</strong></TableCell>
              <TableCell width="12%"><strong>Date</strong></TableCell>
              <TableCell width="12%"><strong>Type</strong></TableCell>
              <TableCell width="18%"><strong>Description</strong></TableCell>
              <TableCell width="18%"><strong>Deliverables</strong></TableCell>
              {editMode && <TableCell width="10%" align="center"><strong>Actions</strong></TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {milestones.map((item, index) => (
              <TableRow key={index} hover>
                <TableCell>{item.id || ''}</TableCell>
                <TableCell sx={{ fontWeight: 'medium' }}>{item.name || ''}</TableCell>
                <TableCell>{formatDate(item.date)}</TableCell>
                <TableCell>
                  <Chip
                    icon={milestoneTypeIcons[item.type || 'Project Milestone'] || <FlagIcon />}
                    label={item.type || 'Project Milestone'}
                    size="small"
                    color={
                      (item.type || 'Project Milestone') === 'Approval' ? 'success' :
                      (item.type || 'Project Milestone') === 'Deliverable' ? 'primary' :
                      (item.type || 'Project Milestone') === 'Meeting' ? 'info' :
                      'warning'
                    }
                  />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  {item.description || ''}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  {item.deliverables || ''}
                </TableCell>
                {editMode && (
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpenMilestoneDialog(index)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleDeleteMilestone(index)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {editMode && (
              <TableRow>
                <TableCell colSpan={editMode ? 7 : 6} align="center">
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenMilestoneDialog()}
                    variant="outlined"
                    size="small"
                    sx={{ my: 1 }}
                  >
                    Add Milestone
                  </Button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };
  
  // Render visual timeline
  const renderVisualTimeline = () => {
    const phases = editMode ? (editingTimeline.phases || []) : (timeline.phases || []);
    const milestones = editMode ? (editingTimeline.milestones || []) : (timeline.milestones || []);
    
    // Return early if there are no phases
    if (phases.length === 0) return null;
    
    // Calculate project duration and timeline dimensions
    const projectStart = new Date(editMode ? editingTimeline.startDate : timeline.startDate);
    const projectEnd = new Date(editMode ? editingTimeline.endDate : timeline.endDate);
    const totalDuration = calculateDuration(projectStart, projectEnd);
    
    return (
      <Box sx={{ mt: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Timeline Visualization
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Paper variant="outlined" sx={{ p: 2 }}>
          {/* Project timeframe */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Project Start: {formatDate(projectStart)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Project End: {formatDate(projectEnd)}
            </Typography>
          </Box>
          
          {/* Phase timeline */}
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 3 }}>
            {phases.map((phase, index) => {
              const phaseStart = new Date(phase.startDate);
              const phaseEnd = new Date(phase.endDate);
              const phaseDuration = calculateDuration(phaseStart, phaseEnd);
              const phaseWidth = (phaseDuration / totalDuration) * 100;
              
              return (
                <Box key={index} sx={{ position: 'relative', width: `${phaseWidth}%` }}>
                  <Tooltip 
                    title={
                      <React.Fragment>
                        <Typography variant="subtitle2">{phase.name}</Typography>
                        <Typography variant="body2">{formatDate(phaseStart)} - {formatDate(phaseEnd)}</Typography>
                        <Typography variant="body2">{phaseDuration} days</Typography>
                        {phase.description && <Typography variant="body2">{phase.description}</Typography>}
                      </React.Fragment>
                    }
                  >
                    <TimelinePhase 
                      phasecolor={phaseColors[phase.name]}
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: 'white', 
                          fontWeight: 'bold',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          mx: 1
                        }}
                      >
                        {phase.name}
                      </Typography>
                    </TimelinePhase>
                  </Tooltip>
                </Box>
              );
            })}
          </Box>
          
          {/* Milestone markers */}
          {milestones.length > 0 && (
            <Box sx={{ position: 'relative', height: 60, mt: 3, mb: 1 }}>
              <Divider sx={{ position: 'absolute', top: 15, width: '100%' }} />
              
              {milestones.map((milestone, index) => {
                const milestoneDate = new Date(milestone.date);
                const daysFromStart = differenceInDays(milestoneDate, projectStart);
                const position = (daysFromStart / totalDuration) * 100;
                
                return (
                  <Tooltip
                    key={index}
                    title={
                      <React.Fragment>
                        <Typography variant="subtitle2">{milestone.name}</Typography>
                        <Typography variant="body2">Date: {formatDate(milestoneDate)}</Typography>
                        <Typography variant="body2">Type: {milestone.type}</Typography>
                        {milestone.description && <Typography variant="body2">{milestone.description}</Typography>}
                      </React.Fragment>
                    }
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        left: `${position}%`,
                        top: 0,
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      {milestoneTypeIcons[milestone.type] || <FlagIcon 
                        sx={{ 
                          color: milestone.type === 'Approval' ? 'success.main' :
                                 milestone.type === 'Deliverable' ? 'primary.main' :
                                 milestone.type === 'Meeting' ? 'info.main' :
                                 'warning.main'
                        }} 
                      />}
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          mt: 1,
                          maxWidth: 80,
                          textAlign: 'center',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {milestone.name}
                      </Typography>
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          )}
        </Paper>
      </Box>
    );
  };

  const hasExistingTimeline =
    Boolean(timeline.overview) ||
    timeline.phases.length > 0 ||
    timeline.milestones.length > 0;
  
  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <SectionTitle variant="h5">
            <TimelineIcon />
            Project Timeline
          </SectionTitle>
          <Box>
            {editMode ? (
              <>
                <Button
                  startIcon={<SaveIcon />}
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  sx={{ mr: 1 }}
                >
                  Save
                </Button>
                <Button
                  startIcon={<CancelIcon />}
                  variant="outlined"
                  color="error"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button
                  startIcon={<AutoAwesomeIcon />}
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    hasExistingTimeline ? setFetchConfirmOpen(true) : generateTimelineWithAI();
                  }}
                  disabled={loading || aiLoading || !getSowDocument()}
                  sx={{ mr: 1 }}
                >
                  {loading
                    ? 'Loading...'
                    : aiLoading
                    ? hasExistingTimeline ? 'Regenerating...' : 'Fetching...'
                    : hasExistingTimeline ? 'Regenerate from Project Documents' : 'Fetch from Project Documents'}
                </Button>
                <Button
                  startIcon={<EditIcon />}
                  variant="outlined"
                  color="primary"
                  onClick={handleEdit}
                >
                  Edit
                </Button>
              </>
            )}
          </Box>
        </Box>
        
        <Typography variant="body1" paragraph>
          Define the project timeline, milestones, and key deliverables for the SAP implementation project.
        </Typography>
        
        {!getSowDocument() && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            No project documents found. Upload project documents to enable AI-assisted content generation.
          </Alert>
        )}
        
        {primarySource && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Content generated from: {primarySource.filename || primarySource.document_name || 'Project Document'}
          </Alert>
        )}
        
        {aiLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}
        
        {/* Overview and Project Dates */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={8}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Timeline Overview
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {editMode ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    variant="outlined"
                    value={editingTimeline.overview || ''}
                    onChange={handleOverviewChange}
                    placeholder="Enter an overview of the project timeline"
                  />
                ) : (
                  <Typography variant="body1" paragraph>
                    {timeline.overview || ''}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Project Timeframe
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {editMode ? (
                  <Stack spacing={3}>
                    <TextField
                      type="date"
                      label="Project Start Date"
                      value={editingTimeline.startDate ? format(editingTimeline.startDate instanceof Date ? editingTimeline.startDate : new Date(editingTimeline.startDate), 'yyyy-MM-dd') : ''}
                      onChange={(e) => { const d = new Date(e.target.value); if (!isNaN(d.getTime())) handleProjectDateChange('startDate', d); }}
                      fullWidth
                      margin="normal"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      type="date"
                      label="Project End Date"
                      value={editingTimeline.endDate ? format(editingTimeline.endDate instanceof Date ? editingTimeline.endDate : new Date(editingTimeline.endDate), 'yyyy-MM-dd') : ''}
                      onChange={(e) => { const d = new Date(e.target.value); if (!isNaN(d.getTime())) handleProjectDateChange('endDate', d); }}
                      fullWidth
                      margin="normal"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Stack>
                ) : (
                  <Box>
                    <Typography variant="body1">
                      <strong>Start Date:</strong> {formatDate(timeline.startDate)}
                    </Typography>
                    <Typography variant="body1">
                      <strong>End Date:</strong> {formatDate(timeline.endDate)}
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      <strong>Duration:</strong> {calculateDuration(timeline.startDate, timeline.endDate)} days
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        {/* Visual Timeline */}
        {!editMode && renderVisualTimeline()}
        
        {/* Phases */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <ScheduleIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" color="primary">
              Project Phases
            </Typography>
          </Box>
          
          {renderPhasesTable()}
        </Box>
        
        {/* Milestones */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FlagIcon color="secondary" sx={{ mr: 1 }} />
            <Typography variant="h6" color="secondary">
              Project Milestones
            </Typography>
          </Box>
          
          {renderMilestonesTable()}
        </Box>
      </Paper>
      
      {/* AI Response Accordion (if available) */}
      {aiResponse && (
        <Accordion sx={{ mb: 3 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>AI-Generated Content (Raw Response)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Paper sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                {aiResponse}
              </Typography>
            </Paper>
          </AccordionDetails>
        </Accordion>
      )}
      
      {/* Phase Dialog */}
      <Dialog open={openPhaseDialog} onClose={handleClosePhaseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogItemIndex === null ? 'Add' : 'Edit'} Project Phase
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={4}>
              <TextField
                name="id"
                label="Phase ID"
                value={phaseItem.id}
                onChange={handlePhaseInputChange}
                fullWidth
                variant="outlined"
                margin="dense"
                required
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="phase-name-label">Phase Name</InputLabel>
                <Select
                  labelId="phase-name-label"
                  name="name"
                  value={phaseItem.name}
                  onChange={handlePhaseInputChange}
                  label="Phase Name"
                >
                  <MenuItem value="Prepare">Prepare</MenuItem>
                  <MenuItem value="Explore">Explore</MenuItem>
                  <MenuItem value="Realize">Realize</MenuItem>
                  <MenuItem value="Deploy">Deploy</MenuItem>
                  <MenuItem value="Run">Run</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                type="date"
                label="Start Date"
                value={phaseItem.startDate ? format(phaseItem.startDate instanceof Date ? phaseItem.startDate : new Date(phaseItem.startDate), 'yyyy-MM-dd') : ''}
                onChange={(e) => { const d = new Date(e.target.value); if (!isNaN(d.getTime())) handlePhaseDate('startDate', d); }}
                fullWidth
                margin="dense"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                type="date"
                label="End Date"
                value={phaseItem.endDate ? format(phaseItem.endDate instanceof Date ? phaseItem.endDate : new Date(phaseItem.endDate), 'yyyy-MM-dd') : ''}
                onChange={(e) => { const d = new Date(e.target.value); if (!isNaN(d.getTime())) handlePhaseDate('endDate', d); }}
                fullWidth
                margin="dense"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="description"
                label="Description"
                value={phaseItem.description}
                onChange={handlePhaseInputChange}
                fullWidth
                variant="outlined"
                margin="dense"
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePhaseDialog}>Cancel</Button>
          <Button 
            onClick={handleSavePhase} 
            variant="contained" 
            disabled={!phaseItem.name || !phaseItem.id}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Milestone Dialog */}
      <Dialog open={openMilestoneDialog} onClose={handleCloseMilestoneDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogItemIndex === null ? 'Add' : 'Edit'} Project Milestone
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={4}>
              <TextField
                name="id"
                label="Milestone ID"
                value={milestoneItem.id}
                onChange={handleMilestoneInputChange}
                fullWidth
                variant="outlined"
                margin="dense"
                required
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                name="name"
                label="Milestone Name"
                value={milestoneItem.name}
                onChange={handleMilestoneInputChange}
                fullWidth
                variant="outlined"
                margin="dense"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                type="date"
                label="Milestone Date"
                value={milestoneItem.date ? format(milestoneItem.date instanceof Date ? milestoneItem.date : new Date(milestoneItem.date), 'yyyy-MM-dd') : ''}
                onChange={(e) => { const d = new Date(e.target.value); if (!isNaN(d.getTime())) handleMilestoneDate(d); }}
                fullWidth
                margin="dense"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="milestone-type-label">Milestone Type</InputLabel>
                <Select
                  labelId="milestone-type-label"
                  name="type"
                  value={milestoneItem.type}
                  onChange={handleMilestoneInputChange}
                  label="Milestone Type"
                >
                  <MenuItem value="Project Milestone">Project Milestone</MenuItem>
                  <MenuItem value="Deliverable">Deliverable</MenuItem>
                  <MenuItem value="Approval">Approval</MenuItem>
                  <MenuItem value="Meeting">Meeting</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="description"
                label="Description"
                value={milestoneItem.description}
                onChange={handleMilestoneInputChange}
                fullWidth
                variant="outlined"
                margin="dense"
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="deliverables"
                label="Deliverables"
                value={milestoneItem.deliverables}
                onChange={handleMilestoneInputChange}
                fullWidth
                variant="outlined"
                margin="dense"
                multiline
                rows={2}
                placeholder="List key deliverables associated with this milestone"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMilestoneDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveMilestone} 
            variant="contained" 
            disabled={!milestoneItem.name || !milestoneItem.id}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <SafetyConfirmDialog
        open={fetchConfirmOpen}
        title={hasExistingTimeline ? 'Regenerate Project Timeline from Project Documents?' : 'Fetch Project Timeline from Project Documents?'}
        warning={hasExistingTimeline ? 'The complete current Project Timeline will be replaced.' : 'Project Timeline will be generated from Project Documents.'}
        paragraphs={[
          hasExistingTimeline
            ? 'This action will use AI to regenerate the complete project timeline, including dates, phases, milestones, and descriptions.'
            : 'This action will use AI to fetch the project timeline, including dates, phases, milestones, and descriptions from Project Documents.',
          ...(hasExistingTimeline ? ['Any manual edits made to the existing Project Timeline will be permanently lost.'] : [])
        ]}
        confirmLabel={hasExistingTimeline ? 'Yes, Regenerate Timeline' : 'Yes, Fetch Timeline'}
        loading={aiLoading}
        onCancel={() => setFetchConfirmOpen(false)}
        onConfirm={() => { setFetchConfirmOpen(false); generateTimelineWithAI(); }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProjectTimelinePage;
